import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import {
  buildWorkspaceColumns,
  buildWorkspaceSearchMenuOptions,
  sortWorkspaceItems,
  toWorkspaceListItem,
  type WorkspaceItem,
  type WorkspaceSearchMenuHandlers,
  type WorkspacesListItem,
} from '@bizzdesign/data-access-workspaces';
import { type SearchScope } from '@bizzdesign/sdk-search';
import { type Item, type TableColumn } from '@bizzdesign/shared/models';
import { SearchResultsDisplayBase } from '../search-results-display.base';
import { searchResultsTabImports } from '../search-results-tab.imports';
import { type SearchTabEmptyStateLink } from '../search-tab-empty-state/search-tab-empty-state.component';
import {
  type DisplayOption,
  lastOpenedOption,
  type SortOption,
  workspacesSortSettings,
} from '@bizzdesign/workspaces/utils';

@Component({
  selector: 'bd-workspace-search-results',
  imports: [...searchResultsTabImports],
  templateUrl: './workspace-search-results.component.html',
  styleUrl: '../search-results-tabs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspaceSearchResultsComponent extends SearchResultsDisplayBase {
  workspaceItems = input<WorkspaceItem[]>([]);
  loading = input<boolean>(false);
  totalCount = input<number>(0);
  currentPageIndex = input<number>(0);
  pageSize = input<number>(20);
  filtersHeight = input<number>(0);
  frameCounts = input<Record<string, number>>({});
  frameCountsLoading = input<Record<string, boolean>>({});

  openWorkspace = output<WorkspaceItem>();
  openWorkspaceDetails = output<WorkspaceItem>();
  inviteToWorkspace = output<WorkspaceItem>();
  copyWorkspaceLink = output<WorkspaceItem>();
  archiveWorkspace = output<WorkspaceItem>();
  unarchiveWorkspace = output<WorkspaceItem>();
  switchScope = output<SearchScope>();
  pageChange = output<number>();

  emptyStateLinks = input<SearchTabEmptyStateLink[]>([]);

  readonly sortSettings = workspacesSortSettings;
  readonly displayOption = signal<DisplayOption>('grid');
  readonly sortOption = signal<SortOption>(lastOpenedOption.key);

  private readonly menuHandlers: WorkspaceSearchMenuHandlers = {
    onOpen: (workspaceItem) => this.openWorkspace.emit(workspaceItem),
    onSeeDetails: (workspaceItem) =>
      this.openWorkspaceDetails.emit(workspaceItem),
    onInvite: (workspaceItem) => this.inviteToWorkspace.emit(workspaceItem),
    onCopyLink: (workspaceItem) => this.copyWorkspaceLink.emit(workspaceItem),
    onArchive: (workspaceItem) => this.archiveWorkspace.emit(workspaceItem),
    onUnarchive: (workspaceItem) => this.unarchiveWorkspace.emit(workspaceItem),
  };

  readonly sortedWorkspaceItems = computed((): WorkspaceItem[] =>
    sortWorkspaceItems(this.workspaceItems(), this.sortOption()),
  );

  readonly listItems = computed<WorkspacesListItem[]>(() => {
    const counts = this.frameCounts();
    const loading = this.frameCountsLoading();
    const sortOption = this.sortOption();
    return this.sortedWorkspaceItems().map((workspaceItem) =>
      toWorkspaceListItem(workspaceItem, {
        sortOption,
        frameCount: counts[workspaceItem.id],
        frameCountLoading: loading[workspaceItem.id] ?? true,
        menuOptions: buildWorkspaceSearchMenuOptions(
          workspaceItem,
          this.menuHandlers,
          { includeSeeDetails: true },
        ),
        actionButton: workspaceItem.archived
          ? undefined
          : {
              labelKey: 'workspaces.openWorkspace',
              tScope: 'featureWorkspaces',
              styleClass: 'bd-size-small',
              group: 'neutral',
              type: 'asLink',
              onClick: () => this.openWorkspace.emit(workspaceItem),
            },
        onSelect: () => this.openWorkspaceDetails.emit(workspaceItem),
      }),
    );
  });

  readonly workspaceColumns = computed<TableColumn<WorkspacesListItem>[]>(() =>
    buildWorkspaceColumns(this.sortOption()),
  );

  onSelectedItemsChange(
    item: Item<WorkspaceItem> | Item<WorkspaceItem>[] | undefined,
  ): void {
    if (item && !Array.isArray(item) && item.data) {
      this.openWorkspaceDetails.emit(item.data);
    }
  }

  onItemDoubleClick(item: Item<WorkspaceItem>): void {
    if (item.data && !item.data.archived) {
      this.openWorkspace.emit(item.data);
    }
  }

  onPageChange(pageNumber: number): void {
    this.pageChange.emit(pageNumber);
  }
}
